import React, { Component } from 'react'
import { List, Slider } from 'antd'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { injectIntl } from 'react-intl'
import gql from 'graphql-tag'
import { withApollo } from '@apollo/client/react/hoc'
import { withContentRect } from 'react-measure'
import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons'
import { debounce } from 'lodash'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Loading from '../../components/LoadingPage'
import * as Utils from '../../components/Utils'
import S, { Colors } from '../../components/Styles'
import * as AudioPlayer from '../../components/AudioPlayer'
import { SampleDetailsQuery } from './Queries'
import { getAudioDuration } from '../../utils/audio'

const reorder = (list, startIndex, endIndex) => {
	const result = Array.from(list)
	const [removed] = result.splice(startIndex, 1)
	result.splice(endIndex, 0, removed)

	return result
}

class Listen extends Component {
	state = {
		loading: false,
		saving: false,
		items: [],
		playing: {},
		hoveredTrack: null
	}

	componentDidMount() {
		this.loadTracks(this.props)
	}

	// props is a SampleDetailsQuery sampleById object
	componentWillReceiveProps(props) {
		const shouldLoad =
			props._id !== this.props._id ||
			props.updatedBy?.date !== this.props.updatedBy?.date ||
			props.playlistId !== this.props.playlistId

		if (shouldLoad) {
			this.loadTracks(props)
		}
	}

	loadTracks = async (props) => {
		this.setState({ loading: true })

		let list = []
		for (let i in props.tracks) {
			let track = props.tracks[i]
			let trackObject = props.trackObjects[i]

			list.push({
				...track,
				...trackObject
			})
		}

		this.setState({ loading: false, items: list })
	}

	// this method is debounced because when the slider is moved
	// the setTrackRange is triggered, then the onClick event bubble
	// and also play the track
	playTrack = debounce((track) => {
		AudioPlayer.startTrack({
			track: track._id,
			crossfade: 0,
			startTime: track.start === -1 ? 0 : track.start,
			endTime:
				track.end === -1 ?
					getAudioDuration(track, 'stereo') / 1000
				:	track.end,
			stopAtEnd: true,
			progressCallback: (position) => {
				this.setState({
					playing: { track: track._id, position }
				})
			},
			endCallback: () => {
				this.setState({ playing: {} })
			}
		})
	}, 20)

	stopTrack = () => {
		AudioPlayer.stop(0)
		this.setState({ playing: {} })
	}

	save = async () => {
		this.setState({ saving: true })
		const { client, _id } = this.props

		try {
			let tr = this.state.items.map((t) => ({
				trackId: t._id,
				start: t.start,
				end: t.end
			}))

			const r = await client.mutate({
				variables: {
					id: _id,
					tracks: JSON.stringify(tr)
				},
				mutation: gql`
					mutation updateSampleTracksList(
						$id: String!
						$tracks: String
					) {
						sampleUpdateById(_id: $id, tracks: $tracks) {
							recordId
						}
					}
				`,
				refetchQueries: [
					{
						query: SampleDetailsQuery,
						variables: { id: _id }
					}
				]
			})

			Utils.parseMutationResponse(r)
			let res = r.data.sampleUpdateById

			if (res.message) {
				throw new Error(res.message)
			}
		} catch (e) {
			Utils.displayError(e)
		}
		this.setState({ saving: false })
	}

	onDragEnd = (result) => {
		// dropped outside the list
		if (!result.destination) {
			return
		}

		const items = reorder(
			this.state.items,
			result.source.index,
			result.destination.index
		)

		this.setState({ items }, this.save)
	}

	setTrackRange = (trackIndex, range) => {
		// we do a shallow copy of the tracks and the updated track
		const tracks = [...this.state.items]
		const track = { ...tracks[trackIndex] }

		const previousStart = track.start
		const previousEnd = track.end

		track.start = range[0]
		track.end = range[1]

		// we update the tracks in the state
		tracks[trackIndex] = track

		this.setState({ items: tracks }, () => {
			this.save()

			// we launch the playback after the state have been updated
			if (range[0] !== previousStart) {
				// we start the track at the new start position
				// so the user can check it's correct
				this.playTrackFromRangeStart(trackIndex)
			} else if (range[1] !== previousEnd) {
				// we start the track 10 sec before the end so the user
				// can check quickly if the end position is correct
				this.playTrackFromRangeEnd(trackIndex)
			}
		})
	}

	playTrackFromRangeStart = (trackIndex) => {
		let items = [...this.state.items]
		const t = items[trackIndex]
		this.stopTrack()
		this.playTrack({
			...t
		})
	}

	playTrackFromRangeEnd = (trackIndex) => {
		let items = [...this.state.items]
		const t = items[trackIndex]
		const startPosition = Math.max(items[trackIndex].end - 10, 0)
		this.stopTrack()
		this.playTrack({
			...t,
			start: startPosition
		})
	}

	getItemStyle = (isDragging, draggableStyle) => ({
		userSelect: 'none',
		padding: 10,
		borderBottom: isDragging ? 0 : `1px solid ${Colors.lines}`,
		backgroundColor: '#fff',
		display: 'flex',
		...draggableStyle
	})

	render() {
		if (this.state.loading) {
			return <Loading />
		}

		let playBarWidth = 100
		if (
			this.props.contentRect &&
			this.props.contentRect.bounds &&
			this.props.contentRect.bounds.width
		) {
			playBarWidth = (this.props.contentRect.bounds.width - 30) / 2
		}

		const trackDurationInSeconds = (t) =>
			(getAudioDuration(t, 'stereo') ?? 0) / 1000

		return (
			<div ref={this.props.measureRef}>
				<DragDropContext onDragEnd={this.onDragEnd}>
					<Droppable droppableId="droppable">
						{(provided, snapshot) => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
								style={{
									borderTop: `1px solid ${Colors.lines}`,
									marginTop: 20
								}}
							>
								{this.state.items.map((t, index) => (
									<Draggable
										key={t._id}
										draggableId={t._id}
										index={index}
									>
										{(provided, snapshot) => (
											<div
												ref={provided.innerRef}
												{...provided.draggableProps}
												style={this.getItemStyle(
													snapshot.isDragging,
													provided.draggableProps
														.style
												)}
											>
												<div
													style={{
														flex: 1,
														paddingRight: 10
													}}
													{...provided.dragHandleProps}
												>
													<List.Item.Meta
														avatar={
															<div
																style={{
																	position:
																		'relative',
																	cursor: 'pointer'
																}}
																onMouseEnter={() =>
																	this.setState(
																		{
																			hoveredTrack:
																				t._id
																		}
																	)
																}
																onMouseLeave={() =>
																	this.setState(
																		{
																			hoveredTrack:
																				null
																		}
																	)
																}
																onClick={(
																	e
																) => {
																	e.stopPropagation()
																	if (
																		this
																			.state
																			.playing
																			.track ===
																		t._id
																	) {
																		this.stopTrack()
																	} else {
																		this.playTrack(
																			t
																		)
																	}
																}}
															>
																<img
																	src={Utils.trackArtworkThumbUrl(
																		t
																	)}
																	alt=""
																	style={
																		S.trackCover
																	}
																/>
																{(this.state
																	.hoveredTrack ===
																	t._id ||
																	this.state
																		.playing
																		.track ===
																		t._id) && (
																	<div
																		style={{
																			position:
																				'absolute',
																			top: 0,
																			left: 0,
																			right: 0,
																			bottom: 0,
																			backgroundColor:
																				'rgba(0,0,0,0.5)',
																			display:
																				'flex',
																			alignItems:
																				'center',
																			justifyContent:
																				'center',
																			borderRadius:
																				S
																					.trackCover
																					?.borderRadius ||
																				0
																		}}
																	>
																		<FontAwesomeIcon
																			icon={
																				(
																					this
																						.state
																						.playing
																						.track ===
																					t._id
																				) ?
																					faStop
																				:	faPlay
																			}
																			style={{
																				color: '#fff',
																				fontSize: 16
																			}}
																		/>
																	</div>
																)}
															</div>
														}
														title={t.title}
														description={t.artist}
													/>
												</div>
												<div
													onClickCapture={(e) => {
														if (
															e.target.closest(
																'[class*="ant-slider-handle-1"]'
															)
														) {
															// the user clicked in the start handle
															this.playTrackFromRangeStart(
																index
															)
														} else if (
															e.target.closest(
																'[class*="ant-slider-handle-2"]'
															)
														) {
															// the user clicked in the end handle
															this.playTrackFromRangeEnd(
																index
															)
														}
													}}
													onMouseDownCapture={(e) => {
														// the user clicked in the div but not on the slider
														// we seek into the track
														if (
															!e.target.closest(
																'[class*="ant-slider"]'
															)
														) {
															e.stopPropagation()

															// calculate the position to seek play track here
															const rect =
																e.currentTarget.getBoundingClientRect()
															const x =
																e.clientX -
																rect.left
															const percent =
																x / rect.width // 0 to 1

															const trackDuration =
																getAudioDuration(
																	t,
																	'stereo'
																) / 1000
															const start =
																trackDuration *
																percent
															const end =
																trackDuration

															// we play the track from this position to the end
															this.playTrack({
																...t,
																start,
																end
															})
														}
													}}
													style={{
														flex: 1,
														padding: 0,
														position: 'relative',
														height: 50,
														borderRadius: 3,
														border: `1px solid ${Colors.lines}`
													}}
												>
													{this.state.playing
														.track === t._id && (
														<div
															style={{
																position:
																	'absolute',
																top: 0,
																bottom: 0,
																left:
																	playBarWidth *
																	(this.state
																		.playing
																		.position /
																		(getAudioDuration(
																			t,
																			'stereo'
																		) /
																			1000)),
																width: 1,
																background:
																	Colors.primary
															}}
														/>
													)}

													<Slider
														range={{
															draggableTrack: true
														}}
														min={0}
														max={
															getAudioDuration(
																t,
																'stereo'
															) / 1000
														}
														defaultValue={[
															t.start === -1 ?
																0
															:	t.start,
															t.end === -1 ?
																getAudioDuration(
																	t,
																	'stereo'
																) / 1000
															:	t.end
														]}
														style={{
															margin: '19px 0 0 0'
														}}
														tipFormatter={(v) =>
															Utils.formatMinutes(
																v
															)
														}
														onAfterChange={(
															range
														) =>
															this.setTrackRange(
																index,
																range
															)
														}
													/>
												</div>
											</div>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</div>
		)
	}
}

export default withContentRect('bounds')(withApollo(injectIntl(Listen)))
